import { PropertyFilterField, type PropertyDirectory } from '@/components/property-filter';
import { isoToRange, rangeToIso } from '@/lib/datetime-local';
import {
  Button,
  DatePicker,
  FilterRow,
  ListPage,
  Spinner,
  type AppliedFilter,
  type FilterDef,
} from '@mgrvtechgroup/admin-ds';
import { useMemo, useState } from 'react';

export interface ListFilterPatch {
  propertyId?: string | null;
  status?: string | null;
  /** 시간대 포함 ISO. start와 end는 항상 함께 바뀐다. */
  period?: { start: string; end: string } | null;
}

/**
 * 지점·상태·기간으로 거르는 운영 목록의 공통 툴바. 더미 목록 견본과 같은 한 줄 구조다 —
 * 좌측은 컨트롤(28px), 조건이 걸려 있을 때만 `초기화`가 나오고, 우측은 재조회 상태를 알린다.
 * 값이 바뀌면 바로 적용한다(URL이 단일 출처). 기간은 날짜 단위로 고르고 종료는 다음 날 0시로 보낸다.
 */
export function ListFilterToolbar({
  directory,
  propertyId,
  status,
  statusLabel,
  statusOptions,
  periodStart,
  periodEnd,
  periodPlaceholder,
  isFetching,
  onChange,
  onReset,
}: {
  directory: PropertyDirectory;
  propertyId: string | null;
  status: string | null;
  statusLabel: string;
  statusOptions: readonly { value: string; label: string }[];
  periodStart: string | null;
  periodEnd: string | null;
  periodPlaceholder: string;
  isFetching: boolean;
  onChange: (patch: ListFilterPatch) => void;
  onReset: () => void;
}) {
  const [error, setError] = useState('');
  const defs = useMemo<FilterDef[]>(
    () => [
      {
        key: 'status',
        label: statusLabel,
        pinned: true,
        select: 'single',
        options: statusOptions.map((option) => ({ ...option })),
      },
    ],
    [statusLabel, statusOptions],
  );
  const applied = [propertyId, status, periodStart, periodEnd].some(Boolean);
  const filters: AppliedFilter[] = [
    { id: 'status', fieldKey: 'status', values: status ? [status] : [] },
  ];
  const change = (patch: ListFilterPatch) => {
    setError('');
    onChange(patch);
  };
  return (
    <>
      <ListPage.Toolbar
        left={
          // 좁은 폭에서는 컨트롤을 줄바꿈해 글자가 세로로 쪼개지지 않게 한다.
          <div className="flex w-full flex-wrap items-center gap-2">
            <PropertyFilterField
              value={propertyId ?? ''}
              directory={directory}
              onChange={(value) => {
                change({ propertyId: value || null });
              }}
              onInvalid={setError}
            />
            <FilterRow
              defs={defs}
              value={filters}
              onChange={(next) => {
                change({
                  status: next.find((item) => item.fieldKey === 'status')?.values[0] ?? null,
                });
              }}
            />
            <DatePicker
              mode="range"
              size="sm"
              placeholder={periodPlaceholder}
              value={isoToRange(periodStart, periodEnd)}
              onChange={(range) => {
                change({ period: range ? rangeToIso(range) : null });
              }}
            />
            {applied && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setError('');
                  onReset();
                }}
              >
                초기화
              </Button>
            )}
          </div>
        }
        right={
          <span
            className="caption text-muted-foreground flex items-center gap-1.5"
            aria-live="polite"
          >
            {isFetching && (
              <>
                <Spinner className="size-3.5" />
                불러오는 중
              </>
            )}
          </span>
        }
      />
      {error && (
        <p role="alert" className="body-sm text-destructive px-[var(--layout-gutter)] py-2">
          {error}
        </p>
      )}
    </>
  );
}

interface ListFilterKeys {
  propertyId: string;
  status: string;
  periodStart: string;
  periodEnd: string;
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null) {
  if (value) params.set(key, value);
  else params.delete(key);
}

/** 바뀐 조건만 주소에 반영한다. 조건이 바뀌면 이전 커서는 의미가 없으므로 지운다. 정렬은 유지한다. */
export function patchListParams(
  params: URLSearchParams,
  patch: ListFilterPatch,
  keys: ListFilterKeys,
): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('cursor');
  if ('propertyId' in patch) setOrDelete(next, keys.propertyId, patch.propertyId ?? null);
  if ('status' in patch) setOrDelete(next, keys.status, patch.status ?? null);
  if ('period' in patch) {
    setOrDelete(next, keys.periodStart, patch.period?.start ?? null);
    setOrDelete(next, keys.periodEnd, patch.period?.end ?? null);
  }
  return next;
}

/** `초기화` — 조건만 지우고 정렬은 남긴다. */
export function clearListParams(params: URLSearchParams, keys: ListFilterKeys): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('cursor');
  for (const key of [keys.propertyId, keys.status, keys.periodStart, keys.periodEnd])
    next.delete(key);
  return next;
}
